import { notify } from '@affine/component';
import { DebugLogger } from '@affine/debug';
import {
  getOrCreateI18n,
  i18nCompletenesses,
  type Language,
  SUPPORTED_LANGUAGES,
} from '@affine/i18n';
import { effect, Entity, fromPromise, LiveData } from '@toeverything/infra';
import { catchError, EMPTY, exhaustMap, map, tap } from 'rxjs';

import type { GlobalState } from '../../storage';

export type LanguageInfo = {
  key: Language;
  name: string;
  originalName: string;
  completeness: number;
};

const logger = new DebugLogger('i18n');

const LANGUAGE_STORAGE_KEY = 'i18n_lng';
const LANGUAGE_SOURCE_STORAGE_KEY = 'i18n_lng_source';
const USER_LANGUAGE_SOURCE = 'user';

function mapLanguageInfo(language: Language = 'en'): LanguageInfo {
  const languageInfo = SUPPORTED_LANGUAGES[language];

  return {
    key: language,
    name: languageInfo.name,
    originalName: languageInfo.originalName,
    completeness: i18nCompletenesses[language],
  };
}

export class I18n extends Entity {
  private readonly i18n: ReturnType<typeof getOrCreateI18n>;

  private readonly initialLanguage: Language;

  private initialized = false;

  get i18next() {
    return this.i18n;
  }

  readonly currentLanguageKey$: LiveData<Language>;

  readonly currentLanguage$: LiveData<LanguageInfo>;

  readonly languageList: Array<LanguageInfo> = (() => {
    const all: LanguageInfo[] = (
      Object.keys(SUPPORTED_LANGUAGES) as Language[]
    ).map(mapLanguageInfo);
    // Pin English and German at top, sort rest by completeness descending
    const pinned = ['en', 'de'];
    const top = pinned.map(k => all.find(l => l.key === k)!).filter(Boolean);
    const rest = all
      .filter(l => !pinned.includes(l.key))
      .sort((a, b) => b.completeness - a.completeness);
    return [...top, ...rest];
  })();

  constructor(private readonly state: GlobalState) {
    super();
    const initialLanguage = this.resolveInitialLanguage();

    this.initialLanguage = initialLanguage;
    this.i18n = getOrCreateI18n(initialLanguage);
    this.currentLanguageKey$ = LiveData.from(
      this.state
        .watch<Language>(LANGUAGE_STORAGE_KEY)
        .pipe(map(language => this.resolveWatchedLanguage(language))),
      initialLanguage
    );
    this.currentLanguage$ = this.currentLanguageKey$
      .distinctUntilChanged()
      .map(mapLanguageInfo);

    this.i18n.on('languageChanged', (language: Language) => {
      const supportedLanguage = this.normalizeSupportedLanguage(language);
      if (!supportedLanguage) return;

      this.setDocumentLanguage(supportedLanguage);
      this.currentLanguageKey$.value = supportedLanguage;
    });
    this.setDocumentLanguage(initialLanguage);
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.i18n.changeLanguage(this.initialLanguage).catch(error => {
      logger.error('Failed to initialize language', error);
    });
  }

  /**
   * Detect the best matching supported language from browser settings.
   * Checks navigator.languages (ordered by preference) then navigator.language.
   * Returns 'en' if no supported language matches.
   */
  private resolveInitialLanguage(): Language {
    const stateLanguage = this.readTrustedStateLanguage();
    if (stateLanguage) {
      return stateLanguage;
    }

    this.clearUntrustedStateLanguage();
    return this.detectBrowserLanguage();
  }

  private readTrustedStateLanguage(): Language | undefined {
    if (this.readLanguageSource() !== USER_LANGUAGE_SOURCE) return undefined;
    return this.readLanguageFrom(this.state);
  }

  private readLanguageSource(): string | undefined {
    try {
      return this.state.get<string>(LANGUAGE_SOURCE_STORAGE_KEY);
    } catch (error) {
      logger.warn('Failed to read state language source', error);
      return undefined;
    }
  }

  private readLanguageFrom(storage: GlobalState): Language | undefined {
    try {
      return this.normalizeSupportedLanguage(storage.get<Language>('i18n_lng'));
    } catch (error) {
      logger.warn('Failed to read state language', error);
      return undefined;
    }
  }

  private normalizeSupportedLanguage(language: string | undefined) {
    return language && SUPPORTED_LANGUAGES[language as Language]
      ? (language as Language)
      : undefined;
  }

  private setDocumentLanguage(language: Language) {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }

  private persistUserLanguage(language: Language) {
    try {
      // Source first: the language watch trusts a stored language only once
      // its source says the user picked it, else it falls back to the start
      // language and the settings vault saves that one.
      this.state.set(LANGUAGE_SOURCE_STORAGE_KEY, USER_LANGUAGE_SOURCE);
      this.state.set(LANGUAGE_STORAGE_KEY, language);
    } catch (error) {
      logger.warn('Failed to persist language', error);
    }
  }

  private clearUntrustedStateLanguage() {
    try {
      if (!this.state.get(LANGUAGE_STORAGE_KEY)) return;
      this.state.del(LANGUAGE_STORAGE_KEY);
      this.state.del(LANGUAGE_SOURCE_STORAGE_KEY);
    } catch (error) {
      logger.warn('Failed to clear untrusted state language', error);
    }
  }

  private resolveWatchedLanguage(language: Language | undefined): Language {
    if (this.readLanguageSource() === USER_LANGUAGE_SOURCE) {
      const normalizedLanguage = this.normalizeSupportedLanguage(language);
      if (normalizedLanguage) return normalizedLanguage;
    }

    return this.initialLanguage;
  }

  private detectBrowserLanguage(): Language {
    const candidates =
      typeof navigator !== 'undefined'
        ? [...(navigator.languages || []), navigator.language].filter(Boolean)
        : [];

    const supported = new Set(Object.keys(SUPPORTED_LANGUAGES));

    for (const candidate of candidates) {
      // Exact match (e.g. "de", "zh-Hans")
      const exact = this.normalizeSupportedLanguage(candidate);
      if (exact) return exact;
      // Base language match (e.g. "de-DE" → "de", "en-US" → "en")
      const base = candidate.split('-')[0];
      if (supported.has(base as Language)) return base as Language;
    }

    return 'en';
  }

  changeLanguage = effect(
    exhaustMap((language: string) => {
      const supportedLanguage = this.normalizeSupportedLanguage(language);
      if (!supportedLanguage) {
        logger.warn(`Ignoring unsupported language: ${language}`);
        return EMPTY;
      }

      return fromPromise(() =>
        this.i18n.changeLanguage(supportedLanguage)
      ).pipe(
        tap(() => {
          this.persistUserLanguage(supportedLanguage);
        }),
        catchError(error => {
          notify({
            theme: 'error',
            title: 'Failed to change language',
            message: 'Error occurs when loading language files',
          });

          logger.error('Failed to change language', error);

          return EMPTY;
        })
      );
    })
  );
}
